import React, { FC, HTMLAttributes, useCallback, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { useQuery } from '@redux-requests/react';
import { FaIcon, IconButtonWrapper } from 'components/ui';
import { logoutAction } from 'modules/auth/store/actions';
import { useOnClickOutside } from 'hooks';
import {
  IDeveloperProfile,
  toggleAccountDetails,
  togglePasswordModal,
} from 'modules/layout/store/actions';
import { AccountConstants } from 'modules/layout/store/constants';
import { t } from 'utils';
import styles from './Account.module.scss';

export interface IAccount extends HTMLAttributes<HTMLDivElement> {
  img?: string;
}

const Account: FC<IAccount> = (props) => {
  const { img, className } = props;
  const {
    data: { firstName = 'User', lastName = 'Last name' },
  }: { data: IDeveloperProfile } = useQuery({ type: AccountConstants.ACCOUNT });
  const dispatch = useDispatch();
  const [hideAccount, setHideAccount] = useState(true);
  const accountRef = useRef<HTMLDivElement>(null);
  const rootClassName = classNames(styles.root, className, {
    [styles.hidden]: hideAccount,
  });
  const logoContainerIcon = hideAccount ? 'chevron-down' : 'chevron-up';
  const usersInitials = useMemo(
    () => `${firstName.charAt(0)}${lastName.charAt(0)}`,
    [firstName, lastName]
  );

  const toggleAccountHandler = useCallback((e) => {
    e.stopPropagation();
    setHideAccount((prev) => !prev);
  }, []);

  const closeAccountHandler = useCallback(() => {
    setHideAccount(true);
  }, []);

  useOnClickOutside(accountRef, closeAccountHandler);

  const handleAccountDetailsClick = useCallback(() => {
    dispatch(toggleAccountDetails(true));
    setHideAccount(true);
  }, [dispatch]);

  const handlePasswordClick = useCallback(() => {
    dispatch(togglePasswordModal(true));
    setHideAccount(true);
  }, [dispatch]);

  const signOutHandler = useCallback(() => {
    dispatch(logoutAction());
  }, [dispatch]);

  return (
    <div className={rootClassName} ref={accountRef}>
      <div className={styles.accountContainer}>
        <IconButtonWrapper className={styles.logoContainer} onClick={toggleAccountHandler} noMargin>
          {img && <img src={img} alt='User avatar' />}
          <div className={styles.initialsContainer}>
            <span className={styles.initials}>{usersInitials}</span>
          </div>
        </IconButtonWrapper>
        <div className={styles.openButton} onClick={toggleAccountHandler} role='presentation'>
          <FaIcon icon={logoContainerIcon} />
        </div>
      </div>
      <div className={styles.accountPopup}>
        <p className={styles.greeting}>
          {t('account.name.greeting', [{ key: 'NAME', value: firstName }])}
        </p>
        <IconButtonWrapper
          className={styles.accountButtons}
          onClick={handleAccountDetailsClick}
          icon='user-ninja'
          iconPrefix='far'
        >
          {t('account.details')}
        </IconButtonWrapper>
        <IconButtonWrapper
          className={styles.accountButtons}
          onClick={handlePasswordClick}
          icon='shield-alt'
          iconPrefix='far'
        >
          {t('password')}
        </IconButtonWrapper>
        <IconButtonWrapper
          onClick={signOutHandler}
          className={styles.accountButtons}
          icon='lock-open'
          iconPrefix='far'
        >
          {t('sign.out')}
        </IconButtonWrapper>
      </div>
    </div>
  );
};

export default Account;
